/**
 * A single crawlable, playable trivia page at `/trivia/:slug` (#295).
 *
 * The page is derived from a committed {@link import('@jeopardy/shared').TriviaBoard}: its
 * pooled Q&A renders as `<details>` rows so the answers live in the crawlable DOM but stay
 * hidden by default, and a prominent "Play this game" CTA launches the WHOLE board as a
 * free HUMAN-hosted PACK game (`POST /api/trivia/games`) — the visitor becomes the host
 * (shared screen; others join by code as players). The route mints the host ticket, so
 * the CTA hands it to {@link ./HostPage} via router navigation state (no gated `/ticket`
 * round trip). HUMAN host (not AUTO) because an anonymous visitor has no credits, and a
 * HUMAN pack game is free (#44 D-44.3) while AUTO would need TTS/credits.
 *
 * SSR-safe: rendered by the build-time prerender into `dist/trivia/<slug>/index.html` with
 * a `FAQPage` JSON-LD in `<head>` (from {@link triviaHeadFor}, matching the on-page Q&A).
 * `renderToStaticMarkup` skips effects, so the funnel record + network calls never fire
 * there. During the prerender the slug is passed as a prop (no `Routes` match for
 * `:slug`); in the live app it comes from `useParams`. An unknown/unpublished slug renders
 * a friendly not-found body and marks the page `noindex`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TRIVIA_BOARDS, triviaBoardForSlug, triviaPageBySlug } from '@jeopardy/shared';
import { BRAND_NAME } from '../ui/brand';
import { LandingHeader } from '../ui/landing/LandingHeader';
import { useDocumentHead } from '../seo/useDocumentHead';
import { triviaHeadFor } from '../seo/route-seo';
import type { DocumentHead } from '../seo/head-tags';
import { createTriviaGame } from '../net/pack-api';
import { recordFunnelStage } from '../net/rum';

export function TriviaSet({ slug: slugProp }: { slug?: string } = {}): React.JSX.Element {
  const params = useParams<{ slug: string }>();
  const slug = slugProp ?? params.slug ?? '';
  const navigate = useNavigate();

  // Memoized by slug so `page`/`board` keep a stable identity across re-renders (e.g. the
  // launching-state toggle) — otherwise the head + funnel-view effects below would re-fire
  // every render.
  const page = useMemo(() => triviaPageBySlug(TRIVIA_BOARDS, slug), [slug]);
  const board = useMemo(() => triviaBoardForSlug(TRIVIA_BOARDS, slug), [slug]);

  // One head model per render — the page's own (title/desc/canonical + FAQPage JSON-LD)
  // when published, else a `noindex` not-found head. Kept stable so the hook only reapplies
  // when the slug's resolution changes.
  const head = useMemo<DocumentHead>(
    () =>
      page
        ? triviaHeadFor(page.slug)!
        : {
            title: `Trivia not found · ${BRAND_NAME}`,
            description: 'That trivia set could not be found.',
            canonicalPath: '/trivia',
            robots: 'noindex',
          },
    [page],
  );
  useDocumentHead(head);

  // Funnel seam (#148/#295): the page was viewed. Guarded so a not-found slug isn't
  // counted; the hook itself always runs (stable hook order across renders).
  useEffect(() => {
    if (page) recordFunnelStage('trivia_page_view', { props: { slug } });
  }, [page, slug]);

  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  const onPlay = useCallback(async () => {
    setLaunching(true);
    setLaunchError(null);
    try {
      recordFunnelStage('trivia_play_clicked', { props: { slug } });
      const game = await createTriviaGame(slug);
      // The visitor HOSTS the freshly-launched board (shared screen; others join by
      // code as players). The route already minted a host ticket for the game it just
      // created, so hand it straight to the host page — no gated /ticket round trip.
      navigate('/host', {
        state: { hostTicket: game.hostTicket, joinCode: game.joinCode },
      });
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'Could not start the game.');
      setLaunching(false);
    }
  }, [slug, navigate]);

  if (!page) {
    return (
      <div className="landing" data-testid="trivia-set-notfound">
        <LandingHeader />
        <section className="landing-wrap landing-section landing-why">
          <div className="landing-sec-head">
            <h2>That trivia set could not be found</h2>
            <p>The link may be out of date. Browse the full set of trivia pages.</p>
          </div>
          <Link className="btn btn--primary landing-cta" to="/trivia">
            Browse trivia
          </Link>
        </section>
      </div>
    );
  }

  // Related sibling pages that still resolve to a published page (title for the link).
  const relatedPages = page.related
    .map((relatedSlug) => triviaPageBySlug(TRIVIA_BOARDS, relatedSlug))
    .filter((p): p is NonNullable<typeof p> => p !== undefined);

  return (
    <div className="landing" data-testid="trivia-set-page">
      <LandingHeader />

      <header className="landing-wrap landing-hero">
        <div className="landing-hero__copy">
          <span className="landing-eyebrow">
            <Link to="/trivia">Trivia</Link> · {page.theme}
          </span>
          <h1 className="landing-hero__title">{page.title}</h1>
          <p className="landing-hero__lead">{page.description}</p>
          <div className="landing-cta-row" data-testid="trivia-chips">
            <span className="chip">{page.theme}</span>
            {board?.audience && <span className="chip">{board.audience}</span>}
          </div>
        </div>
      </header>

      <section className="landing-wrap landing-section" data-testid="trivia-qa">
        <div className="faq-list">
          {page.items.map((item, i) => (
            <details
              className="faq-item"
              key={`${item.prompt}-${i}`}
              data-testid="trivia-item"
            >
              <summary className="faq-q">
                <span className="trivia-label">Clue</span>
                {item.prompt}
              </summary>
              <p className="faq-a" data-testid="trivia-answer">
                <span className="trivia-label">Answer</span>
                {item.response}
              </p>
            </details>
          ))}
        </div>
      </section>

      <section
        className="landing-wrap landing-section landing-why"
        data-testid="trivia-play-cta"
      >
        <div className="landing-sec-head">
          <h2>Play this board with friends</h2>
          <p>
            No hardware and no app — everyone joins from their phone as a buzzer while the
            board runs on one shared screen.
          </p>
        </div>
        <div className="landing-cta-row">
          <button
            type="button"
            className="btn btn--primary landing-cta"
            data-testid="trivia-play"
            onClick={onPlay}
            disabled={launching}
          >
            {launching ? 'Starting…' : 'Play this game'}
          </button>
          <Link
            className="btn btn--ghost landing-cta"
            to="/host"
            data-testid="trivia-create-own"
          >
            Create your own game
          </Link>
        </div>
        {launchError && (
          <p role="alert" className="landing-subnote" data-testid="trivia-play-error">
            {launchError}
          </p>
        )}
      </section>

      {relatedPages.length > 0 && (
        <section
          className="landing-wrap landing-section landing-why"
          data-testid="trivia-related"
        >
          <div className="landing-sec-head">
            <h2>More like this</h2>
          </div>
          <div className="landing-cta-row">
            {relatedPages.map((related) => (
              <Link
                className="btn btn--ghost landing-cta"
                key={related.slug}
                to={`/trivia/${related.slug}`}
                data-testid="trivia-related-link"
              >
                {related.topic}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
